import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    getTicketDetail,
    updateTicketStatus,
    assignTicket,
    addTicketComment,
} from '../../../store/actions/ticketAction';
import { apiRequest } from '../../../services/apiService';
import SurveyListHeader from '../../../component/common/layout/SurveyListHeader';
import SurveyListSidebar from '../../../component/common/layout/SurveyListSidebar';

const SEVERITY_COLORS = {
    low: '#6c757d',
    medium: '#ffc107',
    high: '#fd7e14',
    critical: '#dc3545',
};

const STATUS_COLORS = {
    open: '#dc3545',
    acknowledged: '#17a2b8',
    in_progress: '#ffc107',
    resolved: '#28a745',
    false_positive: '#6c757d',
};

const STATUS_KEYS = {
    open: 'tickets.status.open',
    acknowledged: 'tickets.status.acknowledged',
    in_progress: 'tickets.status.in_progress',
    resolved: 'tickets.status.resolved',
    false_positive: 'tickets.status.false_positive',
};

const ALERT_TYPE_KEYS = {
    brute_force: 'tickets.alert_type.brute_force',
    unauthorized_token: 'tickets.alert_type.unauthorized_token',
    unknown_user_login: 'tickets.alert_type.unknown_user',
    inactive_user_login: 'tickets.alert_type.inactive_user',
    suspicious_ip: 'tickets.alert_type.suspicious_ip',
    after_hours_access: 'tickets.alert_type.after_hours',
    manual: 'tickets.alert_type.manual',
};

const SOURCE_KEYS = {
    system: 'tickets.source.system',
    user: 'tickets.source.user',
};

const Badge = ({ color, children }) => (
    <span style={{
        backgroundColor: color,
        color: '#fff',
        padding: '3px 10px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'capitalize',
    }}>
        {children}
    </span>
);

const InfoRow = ({ label, children }) => (
    <div className="d-flex mb-2">
        <div style={{ width: 140, color: '#728095', fontSize: 13, flexShrink: 0 }}>{label}</div>
        <div style={{ fontSize: 13, color: '#0F2744' }}>{children}</div>
    </div>
);

export default function TicketDetail() {
    const { ticketId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { ticketDetail, detailLoading, updating } = useSelector((state) => state.ticketData);

    const [commentText, setCommentText] = useState('');
    const [statusValue, setStatusValue] = useState('');
    const [assignToValue, setAssignToValue] = useState('');
    const [users, setUsers] = useState([]);

    const M7 = window.modulePermissions?.M7 || {};

    const dateLocale = i18n.language === 'pt' ? 'pt-PT' : i18n.language === 'sq' ? 'sq-AL' : 'en-GB';

    const translateStatus = (status) =>
        STATUS_KEYS[status] ? t(STATUS_KEYS[status]) : status;

    const translateAlertType = (alertType) =>
        ALERT_TYPE_KEYS[alertType] ? t(ALERT_TYPE_KEYS[alertType]) : alertType;

    const translateSeverity = (severity) =>
        severity && t(`tickets.severity.${severity}`, { defaultValue: severity });

    const translateSource = (source) =>
        SOURCE_KEYS[source] ? t(SOURCE_KEYS[source]) : source;

    const formatDate = (d) => {
        if (!d) return '-';
        return new Date(d).toLocaleDateString(dateLocale, {
            day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });
    };

    useEffect(() => {
        if (ticketId) {
            dispatch(getTicketDetail(ticketId));
        }
    }, [ticketId]);

    useEffect(() => {
        if (ticketDetail) {
            setStatusValue(ticketDetail.status || '');
            setAssignToValue(ticketDetail.assigned_to || '');
        }
    }, [ticketDetail]);

    useEffect(() => {
        if (M7.view_all == 1) {
            loadUsers();
        }
    }, []);

    const loadUsers = async () => {
        const { response } = await apiRequest({
            method: 'POST',
            url: '/users/getUserList',
            data: { startingLimit: 0, resultsPerPage: 200, search: '' },
        });
        if (response?.data?.data) {
            setUsers(response.data.data);
        }
    };

    const handleStatusUpdate = async () => {
        if (!statusValue || statusValue === ticketDetail?.status) return;
        const result = await dispatch(updateTicketStatus({
            ticket_id: ticketId,
            status: statusValue,
        }));
        if (result?.success) {
            toast.success(t('tickets.detail.status_updated'));
            dispatch(getTicketDetail(ticketId));
        } else {
            toast.error(t('tickets.detail.status_update_failed'));
        }
    };

    const handleAssign = async () => {
        if (!assignToValue) return;
        const result = await dispatch(assignTicket({
            ticket_id: ticketId,
            assigned_to: assignToValue,
        }));
        if (result?.success) {
            toast.success(t('tickets.detail.assigned'));
            dispatch(getTicketDetail(ticketId));
        } else {
            toast.error(t('tickets.detail.assign_failed'));
        }
    };

    const handleAddComment = async (e) => {
        e.preventDefault();
        if (!commentText.trim()) return;
        const result = await dispatch(addTicketComment({
            ticket_id: ticketId,
            comment_text: commentText.trim(),
        }));
        if (result?.success) {
            toast.success(t('tickets.detail.comment_added'));
            setCommentText('');
            dispatch(getTicketDetail(ticketId));
        } else {
            toast.error(t('tickets.detail.comment_failed'));
        }
    };

    if (detailLoading) {
        return (
            <div className="d-flex flex-column main-outer-wrapper">
                <SurveyListHeader />
                <div className="flex-grow-1 d-flex survey-main-wrapper">
                    <SurveyListSidebar />
                    <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                        <div className="spinner-border text-danger" role="status" />
                    </div>
                </div>
            </div>
        );
    }

    if (!ticketDetail) {
        return (
            <div className="d-flex flex-column main-outer-wrapper">
                <SurveyListHeader />
                <div className="flex-grow-1 d-flex survey-main-wrapper">
                    <SurveyListSidebar />
                    <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                        <p style={{ color: '#728095' }}>{t('tickets.detail.not_found')}</p>
                    </div>
                </div>
            </div>
        );
    }

    const ticket = ticketDetail;
    const statusOptions = Object.keys(STATUS_KEYS);

    return (
        <div className="d-flex flex-column main-outer-wrapper">
            <SurveyListHeader />
            <div className="flex-grow-1 d-flex survey-main-wrapper">
                <SurveyListSidebar />
                <div className="flex-grow-1 dashboard-page-outer">
                    <div className="dashboard-page-wrap" style={{ padding: '24px 32px' }}>
                        <button className="btn btn-sm btn-outline-secondary mb-3"
                            onClick={() => navigate('/admin/tickets')}>
                            &larr; {t('tickets.detail.back')}
                        </button>

                        <div className="row">
                            <div className="col-lg-8">
                                <div style={{
                                    background: '#fff', borderRadius: 8, padding: 24,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 20,
                                }}>
                                    <h5 style={{ fontWeight: 700, color: '#0F2744', marginBottom: 16 }}>{ticket.title}</h5>

                                    {ticket.description && (
                                        <p style={{ color: '#4a5568', fontSize: 14, marginBottom: 20, lineHeight: 1.6 }}>
                                            {ticket.description}
                                        </p>
                                    )}

                                    <InfoRow label={t('tickets.detail.status')}>
                                        <Badge color={STATUS_COLORS[ticket.status]}>
                                            {translateStatus(ticket.status)}
                                        </Badge>
                                    </InfoRow>
                                    <InfoRow label={t('tickets.detail.severity')}>
                                        <Badge color={SEVERITY_COLORS[ticket.severity]}>
                                            {translateSeverity(ticket.severity)}
                                        </Badge>
                                    </InfoRow>
                                    <InfoRow label={t('tickets.detail.alert_type')}>
                                        {translateAlertType(ticket.alert_type)}
                                    </InfoRow>
                                    <InfoRow label={t('tickets.detail.source')}>
                                        <span style={{ textTransform: 'capitalize' }}>
                                            {translateSource(ticket.source)}
                                        </span>
                                    </InfoRow>
                                    <InfoRow label={t('tickets.detail.ip_address')}>{ticket.ip_address || '-'}</InfoRow>
                                    <InfoRow label={t('tickets.detail.raised_by')}>
                                        {ticket.raised_by_name || '-'}{' '}
                                        {ticket.raised_by_email && (
                                            <span style={{ color: '#728095' }}>({ticket.raised_by_email})</span>
                                        )}
                                    </InfoRow>
                                    <InfoRow label={t('tickets.detail.assigned_to')}>
                                        {ticket.assigned_to_name || (
                                            <span style={{ color: '#aaa' }}>{t('tickets.filters.unassigned')}</span>
                                        )}
                                    </InfoRow>
                                    <InfoRow label={t('tickets.detail.created')}>{formatDate(ticket.created_at)}</InfoRow>
                                    {ticket.resolved_at && (
                                        <InfoRow label={t('tickets.detail.resolved')}>
                                            {formatDate(ticket.resolved_at)}{' '}
                                            {t('tickets.detail.resolved_by', { name: ticket.resolved_by_name || '-' })}
                                        </InfoRow>
                                    )}
                                </div>

                                <div style={{
                                    background: '#fff', borderRadius: 8, padding: 24,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                }}>
                                    <h6 style={{ fontWeight: 700, color: '#0F2744', marginBottom: 16 }}>
                                        {t('tickets.detail.comments', { count: ticket.comments?.length || 0 })}
                                    </h6>

                                    {(!ticket.comments || ticket.comments.length === 0) && (
                                        <p style={{ color: '#728095', fontSize: 13 }}>{t('tickets.detail.no_comments')}</p>
                                    )}

                                    {ticket.comments?.map((comment) => (
                                        <div key={comment.comment_id} style={{
                                            borderBottom: '1px solid #f0f0f0',
                                            paddingBottom: 12, marginBottom: 12,
                                        }}>
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <span style={{ fontWeight: 600, fontSize: 13, color: '#0F2744' }}>
                                                    {comment.added_by_name || t('tickets.detail.unknown_user')}
                                                </span>
                                                <span style={{ fontSize: 12, color: '#728095' }}>
                                                    {formatDate(comment.created_at)}
                                                </span>
                                            </div>
                                            <p style={{ fontSize: 13, color: '#4a5568', margin: 0, lineHeight: 1.5 }}>
                                                {comment.comment_text}
                                            </p>
                                        </div>
                                    ))}

                                    {(M7.comment == 1 || ticket.can_comment == 1) && (
                                        <form onSubmit={handleAddComment} className="mt-3">
                                            <textarea
                                                className="form-control form-control-sm"
                                                rows={3}
                                                placeholder={t('tickets.detail.add_comment_placeholder')}
                                                value={commentText}
                                                onChange={(e) => setCommentText(e.target.value)}
                                                style={{ resize: 'vertical' }}
                                            />
                                            <button className="btn btn-sm btn-danger mt-2" type="submit"
                                                disabled={updating || !commentText.trim()}>
                                                {updating ? t('tickets.detail.posting') : t('tickets.detail.post_comment')}
                                            </button>
                                        </form>
                                    )}
                                </div>
                            </div>

                            <div className="col-lg-4">
                                {M7.view_all == 1 && (
                                    <>
                                        <div style={{
                                            background: '#fff', borderRadius: 8, padding: 20,
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 16,
                                        }}>
                                            <label style={{ fontSize: 13, fontWeight: 600, color: '#0F2744', marginBottom: 8, display: 'block' }}>
                                                {t('tickets.detail.update_status')}
                                            </label>
                                            <select className="form-select form-select-sm mb-2"
                                                value={statusValue} onChange={(e) => setStatusValue(e.target.value)}>
                                                {statusOptions.map((status) => (
                                                    <option key={status} value={status}>
                                                        {translateStatus(status)}
                                                    </option>
                                                ))}
                                            </select>
                                            <button className="btn btn-sm btn-danger w-100"
                                                onClick={handleStatusUpdate}
                                                disabled={updating || statusValue === ticket.status}>
                                                {updating ? t('tickets.detail.updating') : t('tickets.detail.update_status')}
                                            </button>
                                        </div>

                                        <div style={{
                                            background: '#fff', borderRadius: 8, padding: 20,
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                        }}>
                                            <label style={{ fontSize: 13, fontWeight: 600, color: '#0F2744', marginBottom: 8, display: 'block' }}>
                                                {t('tickets.detail.assign_to')}
                                            </label>
                                            <select className="form-select form-select-sm mb-2"
                                                value={assignToValue} onChange={(e) => setAssignToValue(e.target.value)}>
                                                <option value="">{t('tickets.detail.select_user')}</option>
                                                {users.map((u) => (
                                                    <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                                                ))}
                                            </select>
                                            <button className="btn btn-sm btn-danger w-100"
                                                onClick={handleAssign}
                                                disabled={updating || !assignToValue}>
                                                {updating ? t('tickets.detail.assigning') : t('tickets.detail.assign_btn')}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
